// Striped tinted "real photo would go here" stand-ins, ported from the
// editorial design's `shared.jsx`. Used until production photography is
// available — see tmp/handoff/README.md "Assets" for the shot list.

import type { CSSProperties } from 'react'

type Tone = 'warm' | 'sage' | 'blush' | 'cool' | 'cream'

const tones: Record<Tone, { a: string; b: string; text: string }> = {
  warm:  { a: '#e9dec9', b: '#dfd1b6', text: '#7a6a52' },
  sage:  { a: '#dde2d4', b: '#c9d2bd', text: '#566353' },
  blush: { a: '#f3dfd2', b: '#ebcdb9', text: '#8a5d44' },
  cool:  { a: '#dde3e6', b: '#c9d4d8', text: '#4f5e63' },
  cream: { a: '#f3ecde', b: '#e8dcc4', text: '#7a6a52' },
}

interface PhotoPlaceholderProps {
  label?: string
  tone?: Tone
  style?: CSSProperties
}

export function PhotoPlaceholder({ label = 'photo', tone = 'warm', style }: PhotoPlaceholderProps) {
  const t = tones[tone]
  return (
    <div style={{ position: 'relative', overflow: 'hidden', background: t.a, ...style }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `repeating-linear-gradient(45deg, ${t.a} 0 14px, ${t.b} 14px 28px)`,
          opacity: 0.6,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
          fontSize: 11,
          color: t.text,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
    </div>
  )
}

interface AvatarPlaceholderProps {
  size?: number
  tone?: Tone
  label?: string
}

const avatarTones: Record<Tone, string> = {
  warm:  '#d8c8a8',
  sage:  '#c0cdb5',
  blush: '#e8c5b0',
  cool:  '#c4d0d4',
  cream: '#e6d8bb',
}

export function AvatarPlaceholder({ size = 40, tone = 'warm', label = 'V' }: AvatarPlaceholderProps) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: avatarTones[tone],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontFamily: "'Fraunces', serif",
        fontWeight: 600,
        fontSize: size * 0.42,
      }}
    >
      {label}
    </div>
  )
}
